/**
 * QorynCommandPalette — spotlight-style command palette for Qoryn.
 *
 * The bottom-left white Q brand mark is its physical launcher; Cmd/Ctrl+K
 * opens it from anywhere. This is the Qoryn Command Palette — the OS-level
 * action surface for the Founder Console. Extensible: today it hosts session
 * commands (Export / Copy / Clear / Voice / Diagnostics / New session);
 * later phases will layer in project- and memory-level actions on the same
 * fuzzy-search + keyboard-nav architecture.
 *
 * Triggers:
 *   • Click on the footer Q button (data-testid=footer-q-btn) in NavRail.
 *   • Cmd/Ctrl + K from anywhere on the page.
 *
 * Closes on:
 *   • Escape key.
 *   • Backdrop click.
 *   • Successful action (except Switch voice submenu, which opens inline).
 *
 * Design:
 *   • Fixed overlay, glass surface, deep-black background, cyan accents.
 *   • Slide-up + fade entrance (~180ms).
 *   • Search input at top with fuzzy substring filter.
 *   • Vertical list of actions with icon, label, shortcut hint,
 *     optional description, and disabled reason.
 *   • Keyboard nav (ArrowUp/Down, Enter, Escape) + mouse hover.
 *   • Inline confirm for destructive actions (Clear transcript,
 *     New session while live).
 *   • Inline voice submenu — no separate route. Each voice row exposes
 *     a small preview mini-player (▶) when the voice is TTS-previewable.
 *
 * Focus contract:
 *   • When opened, palette input takes focus.
 *   • When closed, focus is returned to the element that had focus at
 *     open time (this keeps the text composer usable if the user opened
 *     the palette from mid-typing).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Download,
    Copy,
    Trash2,
    Mic,
    FileText,
    Play,
    Pause,
    Loader2,
    Check,
    ChevronRight,
    ArrowLeft,
    LogOut,
    Command as CmdIcon,
} from "lucide-react";
import { API } from "@/lib/api";

const SHORTCUT_MOD =
    typeof navigator !== "undefined" &&
    /Mac|iPad|iPhone|iPod/i.test(navigator.platform || navigator.userAgent || "")
        ? "⌘"
        : "Ctrl";

/**
 * Simple fuzzy match: returns true if every char of `query` appears in
 * `haystack` in order, case-insensitive. Cheap, deterministic, no deps.
 */
function fuzzyMatch(haystack, query) {
    if (!query) return true;
    const h = haystack.toLowerCase();
    const q = query.toLowerCase();
    let i = 0;
    for (const c of h) {
        if (c === q[i]) i++;
        if (i === q.length) return true;
    }
    return false;
}

/** Read/write the user's persisted voice preference. */
const VOICE_PREF_KEY = "qoryn.voicePref";
export function readVoicePref() {
    try {
        return localStorage.getItem(VOICE_PREF_KEY) || null;
    } catch {
        return null;
    }
}
export function writeVoicePref(voice) {
    try {
        if (voice) localStorage.setItem(VOICE_PREF_KEY, voice);
        else localStorage.removeItem(VOICE_PREF_KEY);
    } catch {
        /* noop */
    }
}

function ActionRow({
    id, icon: Icon, label, hint, shortcut, disabled, disabledReason,
    isActive, onHover, onClick, trailing,
}) {
    return (
        <button
            type="button"
            role="option"
            data-testid={`qal-action-${id}`}
            data-active={isActive ? "true" : "false"}
            aria-selected={isActive}
            aria-disabled={disabled}
            disabled={disabled}
            onMouseEnter={onHover}
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors duration-100 qoryn-focus ${
                disabled
                    ? "opacity-40 cursor-not-allowed"
                    : isActive
                      ? "bg-qoryn-cyan/10 text-qoryn-text"
                      : "text-qoryn-textDim hover:bg-white/[0.04] hover:text-qoryn-text"
            }`}
        >
            <span
                className={`shrink-0 flex items-center justify-center h-7 w-7 rounded-md ${
                    isActive ? "bg-qoryn-cyan/15 text-qoryn-cyan" : "bg-white/[0.03] text-qoryn-textDim"
                }`}
            >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                <span className="font-sans text-[13px] text-qoryn-text truncate">{label}</span>
                {(hint || (disabled && disabledReason)) && (
                    <span className="font-mono-ui text-[10px] tracking-wider uppercase text-qoryn-textMuted truncate">
                        {disabled && disabledReason ? disabledReason : hint}
                    </span>
                )}
            </span>
            {trailing}
            {shortcut && !trailing && (
                <span className="shrink-0 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textMuted">
                    {shortcut}
                </span>
            )}
        </button>
    );
}

export function QorynCommandPalette({
    open,
    onClose,
    // Session context
    turns,
    sessionStartedAt,
    diagnostics,
    connectionState,
    voices,
    previewable = [],
    currentVoice,
    voicePref,
    onSetVoicePref,
    // Action callbacks (wired to real hook methods in FounderConsole)
    onExportTranscript,
    onCopyTranscript,
    onClearTranscript,
    onDiagnosticsSnapshot,
    onStartSession,
    onRestartSessionWithVoice,
    // Auth
    authEmail,
    onSignOut,
}) {
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    // 'root' or 'voices' — inline submenu view.
    const [view, setView] = useState("root");
    // pending destructive-action confirms: 'clear' | 'new' | null
    const [confirm, setConfirm] = useState(null);
    const [copyFlash, setCopyFlash] = useState(false);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const prevFocusedRef = useRef(null);

    // ---------------------------------------------------------------------
    // Voice preview mini-player (iteration 16).
    //   • Single shared <Audio> element — starting a new preview stops the
    //     previous one immediately.
    //   • Fetches /api/realtime/voice-sample?voice=<name>. First hit for a
    //     voice takes ~2-3s (TTS gen); subsequent hits are cached and near
    //     instant. Errors surface as an 'error' state and clear after 1.4s.
    //   • Does NOT touch the live Realtime session audio element — it lives
    //     in its own Audio() instance, so mic/output pipes are unaffected.
    // ---------------------------------------------------------------------
    const previewAudioRef = useRef(null);
    // { voice, status: 'loading'|'playing'|'error' } | null
    const [preview, setPreview] = useState(null);
    const stopPreview = useCallback(() => {
        try {
            const a = previewAudioRef.current;
            if (a) {
                a.pause();
                a.src = "";
            }
        } catch { /* noop */ }
        previewAudioRef.current = null;
        setPreview(null);
    }, []);
    const startPreview = useCallback(async (voice) => {
        // Toggle-off: if the same voice is currently playing/loading, stop.
        if (preview && preview.voice === voice) {
            stopPreview();
            return;
        }
        stopPreview();
        setPreview({ voice, status: "loading" });
        const url = `${API}/realtime/voice-sample?voice=${encodeURIComponent(voice)}`;
        try {
            const audio = new Audio(url);
            previewAudioRef.current = audio;
            audio.addEventListener("ended", () => {
                if (previewAudioRef.current === audio) {
                    previewAudioRef.current = null;
                    setPreview(null);
                }
            });
            audio.addEventListener("error", () => {
                if (previewAudioRef.current === audio) {
                    previewAudioRef.current = null;
                    setPreview({ voice, status: "error" });
                    setTimeout(() => setPreview((p) => (p?.voice === voice ? null : p)), 1400);
                }
            });
            // playing state fires when playback actually begins (network done)
            audio.addEventListener("playing", () => {
                if (previewAudioRef.current === audio) {
                    setPreview({ voice, status: "playing" });
                }
            });
            await audio.play();
        } catch (e) {
            console.warn("[voice-preview] failed", e);
            setPreview({ voice, status: "error" });
            setTimeout(() => setPreview((p) => (p?.voice === voice ? null : p)), 1400);
        }
    }, [preview, stopPreview]);
    // Stop any playing preview when the palette closes.
    useEffect(() => {
        if (!open) stopPreview();
    }, [open, stopPreview]);

    const hasTurns = (turns?.length ?? 0) > 0;
    const hasDiagnostics = (diagnostics?.length ?? 0) > 0;
    const isLive = connectionState === "open" || connectionState === "connecting";

    // Reset internal state whenever the palette closes/opens.
    useEffect(() => {
        if (open) {
            prevFocusedRef.current = document.activeElement;
            setQuery("");
            setActiveIdx(0);
            setView("root");
            setConfirm(null);
            setCopyFlash(false);
            // Focus the search input on next tick so the browser has
            // actually mounted it before we call focus().
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        } else {
            // Return focus to whatever had focus at open time (usually
            // the composer input).
            const el = prevFocusedRef.current;
            prevFocusedRef.current = null;
            if (el && typeof el.focus === "function") {
                // Delay one tick so the modal is fully unmounted before
                // returning focus — prevents Chrome scroll-into-view jump.
                requestAnimationFrame(() => {
                    try { el.focus({ preventScroll: true }); } catch { /* noop */ }
                });
            }
        }
    }, [open]);

    // ---------------- Root-view actions -----------------------------------
    const rootActions = useMemo(() => {
        return [
            {
                id: "export-session",
                icon: Download,
                label: "Export session",
                hint: `Download transcript as .md${hasTurns ? ` · ${turns.length} turn${turns.length === 1 ? "" : "s"}` : ""}`,
                shortcut: `${SHORTCUT_MOD}S`,
                disabled: !hasTurns,
                disabledReason: "Transcript is empty",
                run: () => {
                    onExportTranscript?.();
                    onClose?.();
                },
            },
            {
                id: "copy-transcript",
                icon: Copy,
                label: "Copy transcript",
                hint: hasTurns ? "Copy markdown to clipboard" : "",
                shortcut: `${SHORTCUT_MOD}C`,
                disabled: !hasTurns,
                disabledReason: "Transcript is empty",
                run: async () => {
                    const ok = await onCopyTranscript?.();
                    if (ok) {
                        setCopyFlash(true);
                        setTimeout(() => {
                            setCopyFlash(false);
                            onClose?.();
                        }, 700);
                    } else {
                        onClose?.();
                    }
                },
            },
            {
                id: "clear-transcript",
                icon: Trash2,
                label: "Clear transcript",
                hint: "Erase on-screen turns (session stays live)",
                disabled: !hasTurns,
                disabledReason: "Transcript is empty",
                needsConfirm: "clear",
                run: () => {
                    onClearTranscript?.();
                    onClose?.();
                },
            },
            {
                id: "switch-voice",
                icon: Mic,
                label: "Switch voice",
                hint: currentVoice
                    ? `Current: ${currentVoice}${voicePref && voicePref !== currentVoice ? ` · next: ${voicePref}` : ""}`
                    : voicePref
                      ? `Preferred: ${voicePref}`
                      : "Choose a voice",
                disabled: (voices?.length ?? 0) === 0,
                disabledReason: "Voices unavailable",
                submenu: "voices",
                run: () => setView("voices"),
            },
            {
                id: "diagnostics-snapshot",
                icon: FileText,
                label: "Diagnostics snapshot",
                hint: hasDiagnostics
                    ? `Export ${diagnostics.length} event${diagnostics.length === 1 ? "" : "s"} as JSON`
                    : "",
                disabled: !hasDiagnostics,
                disabledReason: "No diagnostics events yet",
                run: () => {
                    onDiagnosticsSnapshot?.();
                    onClose?.();
                },
            },
            {
                id: "new-session",
                icon: Play,
                label: isLive ? "New session" : "Start new session",
                hint: isLive
                    ? "End current session and reconnect"
                    : "Begin a conversation with Qoryn",
                disabled: false,
                needsConfirm: isLive ? "new" : null,
                run: () => { onStartSession?.(); onClose?.(); },
            },
            {
                id: "sign-out",
                icon: LogOut,
                label: "Sign out",
                hint: authEmail
                    ? `Signed in as ${authEmail}`
                    : "End your session and return to sign-in",
                disabled: !onSignOut,
                disabledReason: "Auth unavailable",
                needsConfirm: "signout",
                run: async () => {
                    try { await onSignOut?.(); } catch { /* AuthProvider surfaces its own error */ }
                    onClose?.();
                },
            },
        ];
    }, [
        hasTurns, hasDiagnostics, isLive, turns?.length, diagnostics?.length,
        currentVoice, voicePref, voices,
        onExportTranscript, onCopyTranscript, onClearTranscript,
        onDiagnosticsSnapshot, onStartSession, onClose,
        authEmail, onSignOut,
    ]);

    const visible = useMemo(
        () => rootActions.filter((a) => fuzzyMatch(`${a.label} ${a.hint || ""}`, query)),
        [rootActions, query],
    );

    // Clamp activeIdx into visible range whenever filter changes.
    useEffect(() => {
        if (visible.length === 0) setActiveIdx(0);
        else if (activeIdx > visible.length - 1) setActiveIdx(0);
    }, [visible.length, activeIdx]);

    // ---------------- Voice submenu ---------------------------------------
    const voiceSubmenu = useMemo(() => {
        if (view !== "voices") return null;
        // Filter voices by query too.
        const items = (voices || []).filter((v) => fuzzyMatch(v, query));
        return items.map((v, i) => ({ id: `voice-${v}`, name: v, index: i }));
    }, [view, voices, query]);

    // ---------------- Keyboard handling -----------------------------------
    const runAction = useCallback((action) => {
        if (!action || action.disabled) return;
        if (action.needsConfirm) {
            setConfirm(action.needsConfirm);
            return;
        }
        action.run();
    }, []);

    // Document-level Escape / arrow handler — reliable regardless of which
    // element currently owns focus. Only mounted while the palette is open.
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                if (confirm) { setConfirm(null); return; }
                if (view === "voices") {
                    setView("root");
                    setQuery("");
                    setActiveIdx(0);
                    requestAnimationFrame(() => inputRef.current?.focus());
                    return;
                }
                onClose?.();
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [open, confirm, view, onClose]);

    const selectVoice = useCallback((voice) => {
        onSetVoicePref?.(voice);
        if (isLive) {
            // Live: end + reconnect with new voice.
            onRestartSessionWithVoice?.(voice);
        }
        onClose?.();
    }, [isLive, onSetVoicePref, onRestartSessionWithVoice, onClose]);

    const onKeyDown = useCallback((e) => {
        // Note: Escape is handled by the document-level useEffect above so
        // it works regardless of focus. This handler covers navigation +
        // Enter only.
        if (view === "voices") {
            const items = voiceSubmenu || [];
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx((i) => Math.min(items.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
                e.preventDefault();
                const v = items[activeIdx];
                if (v) selectVoice(v.name);
            }
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(visible.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            runAction(visible[activeIdx]);
        }
    }, [view, voiceSubmenu, activeIdx, visible, runAction, selectVoice]);

    // ---------------- Render ----------------------------------------------
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="qal-overlay"
                    data-testid="quick-action-launcher"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Qoryn Quick Actions"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-8"
                    onClick={(e) => {
                        // Close only on genuine backdrop click.
                        if (e.target === e.currentTarget) onClose?.();
                    }}
                >
                    {/* backdrop */}
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-black/70 backdrop-blur-md"
                        onClick={onClose}
                    />

                    {/* palette */}
                    <motion.div
                        key="qal-panel"
                        initial={{ y: 32, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 24, opacity: 0, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                        onKeyDown={onKeyDown}
                        className="relative w-full max-w-[520px] rounded-2xl border border-qoryn-border/70 bg-[#0a0d10]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden"
                        style={{ zIndex: 1 }}
                    >
                        {/* Header — search input, back-arrow when in submenu */}
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-qoryn-border/50">
                            {view === "voices" ? (
                                <button
                                    type="button"
                                    data-testid="qal-back"
                                    aria-label="Back to actions"
                                    onClick={() => { setView("root"); setQuery(""); setActiveIdx(0); inputRef.current?.focus(); }}
                                    className="shrink-0 flex items-center justify-center h-6 w-6 rounded-md text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.05] transition-colors qoryn-focus"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5" />
                                </button>
                            ) : (
                                <CmdIcon className="h-3.5 w-3.5 text-qoryn-textMuted shrink-0" />
                            )}
                            <input
                                ref={inputRef}
                                data-testid="qal-input"
                                type="text"
                                placeholder={view === "voices" ? "Filter voices…" : "Search Qoryn actions…"}
                                value={query}
                                onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                                className="flex-1 bg-transparent border-0 outline-none font-sans text-[14px] text-qoryn-text placeholder:text-qoryn-textMuted"
                                autoComplete="off"
                                spellCheck={false}
                            />
                            <span className="shrink-0 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                ESC
                            </span>
                        </div>

                        {/* Body — root list, submenu list, or confirm gate */}
                        <div
                            ref={listRef}
                            role="listbox"
                            aria-label="Actions"
                            data-testid="qal-list"
                            className="max-h-[52vh] overflow-y-auto py-2 qoryn-transcript-scroll"
                        >
                            {confirm && (
                                <div className="px-4 py-3" data-testid="qal-confirm">
                                    <p className="font-sans text-[13px] text-qoryn-text mb-3">
                                        {confirm === "clear"
                                            ? "Erase the on-screen transcript? The live session stays connected."
                                            : confirm === "signout"
                                              ? "Sign out of Qoryn? Any active session will end and you'll return to the sign-in screen."
                                              : "End the current session and start a fresh one?"}
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            data-testid="qal-confirm-cancel"
                                            onClick={() => setConfirm(null)}
                                            className="flex-1 h-8 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04] font-mono-ui text-[10px] tracking-widest uppercase transition-colors qoryn-focus"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="qal-confirm-proceed"
                                            onClick={() => {
                                                const action = rootActions.find((a) => a.needsConfirm === confirm);
                                                setConfirm(null);
                                                action?.run();
                                            }}
                                            className="flex-1 h-8 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase hover:brightness-110 transition-all qoryn-focus"
                                        >
                                            {confirm === "clear"
                                                ? "Clear"
                                                : confirm === "signout"
                                                  ? "Sign out"
                                                  : "End & Restart"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!confirm && view === "root" && (
                                <>
                                    {visible.length === 0 ? (
                                        <div className="px-4 py-6 text-center font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textMuted" data-testid="qal-empty">
                                            No matching actions
                                        </div>
                                    ) : (
                                        <div className="px-2 flex flex-col gap-0.5">
                                            {visible.map((a, i) => (
                                                <ActionRow
                                                    key={a.id}
                                                    id={a.id}
                                                    icon={a.icon}
                                                    label={a.label}
                                                    hint={copyFlash && a.id === "copy-transcript" ? "Copied to clipboard" : a.hint}
                                                    shortcut={a.shortcut}
                                                    disabled={a.disabled}
                                                    disabledReason={a.disabledReason}
                                                    isActive={i === activeIdx}
                                                    onHover={() => setActiveIdx(i)}
                                                    onClick={() => runAction(a)}
                                                    trailing={
                                                        copyFlash && a.id === "copy-transcript"
                                                            ? <Check className="h-3.5 w-3.5 text-qoryn-cyan shrink-0" />
                                                            : a.submenu
                                                              ? <ChevronRight className="h-3.5 w-3.5 text-qoryn-textMuted shrink-0" />
                                                              : null
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {!confirm && view === "voices" && (
                                <div className="px-2 flex flex-col gap-0.5" data-testid="qal-voice-list">
                                    {(voiceSubmenu || []).length === 0 ? (
                                        <div className="px-4 py-6 text-center font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textMuted">
                                            No matching voices
                                        </div>
                                    ) : (
                                        (voiceSubmenu || []).map((v, i) => {
                                            const isSelected = v.name === (voicePref ?? currentVoice);
                                            const isActive = i === activeIdx;
                                            const canPreview = previewable.includes(v.name);
                                            const previewStatus = preview?.voice === v.name ? preview.status : null;
                                            return (
                                                <div
                                                    key={v.id}
                                                    data-testid={`qal-voice-${v.name}`}
                                                    role="option"
                                                    aria-selected={isActive}
                                                    tabIndex={0}
                                                    onMouseEnter={() => setActiveIdx(i)}
                                                    onClick={() => selectVoice(v.name)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            selectVoice(v.name);
                                                        }
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors duration-100 qoryn-focus cursor-pointer ${
                                                        isActive
                                                            ? "bg-qoryn-cyan/10 text-qoryn-text"
                                                            : "text-qoryn-textDim hover:bg-white/[0.04] hover:text-qoryn-text"
                                                    }`}
                                                >
                                                    <span
                                                        className={`shrink-0 flex items-center justify-center h-7 w-7 rounded-md ${
                                                            isSelected ? "bg-qoryn-cyan/15 text-qoryn-cyan" : "bg-white/[0.03] text-qoryn-textDim"
                                                        }`}
                                                    >
                                                        <Mic className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                    </span>
                                                    <span className="flex-1 min-w-0 flex flex-col gap-0.5 text-left">
                                                        <span className="font-sans text-[13px] text-qoryn-text truncate capitalize">{v.name}</span>
                                                        {(isSelected || previewStatus === "error") && (
                                                            <span className={`font-mono-ui text-[10px] tracking-wider uppercase truncate ${previewStatus === "error" ? "text-red-400/90" : "text-qoryn-cyan/80"}`}>
                                                                {previewStatus === "error"
                                                                    ? "preview failed"
                                                                    : isLive && v.name === currentVoice ? "current" : "preferred"}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {/* Voice preview mini-player */}
                                                    <button
                                                        type="button"
                                                        data-testid={`qal-voice-preview-${v.name}`}
                                                        aria-label={canPreview
                                                            ? (previewStatus === "playing" ? `Stop ${v.name} preview` : `Preview ${v.name} voice`)
                                                            : `Preview unavailable for ${v.name}`}
                                                        title={canPreview
                                                            ? (previewStatus === "playing" ? "Stop preview" : "Preview voice (3-sec sample)")
                                                            : `${v.name} is a live-only voice — preview not available`}
                                                        disabled={!canPreview}
                                                        aria-disabled={!canPreview}
                                                        onClick={(e) => { e.stopPropagation(); if (canPreview) startPreview(v.name); }}
                                                        className={`shrink-0 flex items-center justify-center h-6 w-6 rounded-full transition-colors qoryn-focus ${
                                                            !canPreview
                                                                ? "text-qoryn-textMuted/40 cursor-not-allowed"
                                                                : previewStatus === "playing"
                                                                    ? "text-qoryn-cyan bg-qoryn-cyan/10 hover:brightness-110"
                                                                    : "text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.06]"
                                                        }`}
                                                    >
                                                        {previewStatus === "loading" ? (
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        ) : previewStatus === "playing" ? (
                                                            <Pause className="h-3 w-3" strokeWidth={2} />
                                                        ) : (
                                                            <Play className="h-3 w-3" strokeWidth={2} />
                                                        )}
                                                    </button>
                                                    {isLive && v.name !== currentVoice && (
                                                        <span className="shrink-0 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textMuted">
                                                            Restart w/
                                                        </span>
                                                    )}
                                                    {isSelected && <Check className="h-3.5 w-3.5 text-qoryn-cyan shrink-0" />}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer — hint strip */}
                        <div className="flex items-center justify-between px-4 py-2 border-t border-qoryn-border/50 bg-black/30 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            <span data-testid="qal-hint-nav">↑↓ nav · ↵ select · esc close</span>
                            <span>{SHORTCUT_MOD}K</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default QorynCommandPalette;
